import { Box, type Theme } from '@mui/material';
import type { ReactNode } from 'react';
import { radius } from '../../theme/tokens';
import { MIN_FONT_SIZE } from '../../theme/typography';

/**
 * Tone vocabulary for status pills. Mirrors the v2 design language (StatTile
 * badge / Reports status chips): a soft tint background with the saturated tone
 * for the text.
 */
export type PillTone = 'ok' | 'warn' | 'danger' | 'info' | 'accent' | 'neutral';

function toneColours(
  tone: PillTone,
  theme: Theme,
): { bg: string; fg: string; border?: string } {
  switch (tone) {
    case 'ok':
      return { bg: theme.palette.statusSoft.ok, fg: theme.palette.statusInk.ok };
    case 'warn':
      return { bg: theme.palette.statusSoft.warn, fg: theme.palette.statusInk.warn };
    case 'danger':
      return { bg: theme.palette.statusSoft.danger, fg: theme.palette.statusInk.danger };
    case 'info':
      return { bg: theme.palette.statusSoft.info, fg: theme.palette.statusInk.info };
    case 'accent':
      return { bg: theme.palette.accent.tint, fg: theme.palette.primary.main };
    case 'neutral':
    default:
      // A hairline keeps the neutral pill reading as a chip on a raised row:
      // its tint alone is close to the row background.
      return {
        bg: theme.palette.surface.raised,
        fg: theme.palette.text.secondary,
        border: theme.palette.divider,
      };
  }
}

/**
 * `outlined` keeps the tone's ink on no fill, for a state that is the tone's
 * weaker sibling (approved with the timesheet beside approved); `muted` drops
 * a neutral pill's fill, for a state with nothing to do. Its ink stays at
 * `text.secondary`: `text.disabled` fails AA on text that still informs.
 */
export type PillVariant = 'soft' | 'outlined' | 'muted';

interface TonePillProps {
  tone: PillTone;
  label: ReactNode;
  variant?: PillVariant;
  /** Optional leading icon (size it ~13px to sit inside the pill). */
  icon?: ReactNode;
  /**
   * Shrink to the space it is given and end the label with an ellipsis, for a
   * pill in a fixed-width column. Pass `title` with it, so the whole label
   * stays readable on hover.
   */
  truncate?: boolean;
  title?: string;
}

/**
 * Compact soft-tint status pill — the v2 replacement for ad-hoc MUI `<Chip>`
 * status indicators. The project settings sections use the narrower
 * `settings/SettingsPill`, which fixes what each pill means there.
 */
export function TonePill({ tone, label, variant = 'soft', icon, truncate = false, title }: TonePillProps) {
  return (
    <Box
      component="span"
      title={title}
      sx={(theme) => {
        const soft = toneColours(tone, theme);
        const { bg, fg, border } =
          variant === 'outlined'
            ? { bg: 'transparent', fg: soft.fg, border: soft.fg }
            : variant === 'muted'
              ? { bg: 'transparent', fg: theme.palette.text.secondary, border: theme.palette.divider }
              : soft;
        return {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          bgcolor: bg,
          color: fg,
          border: border ? `1px solid ${border}` : undefined,
          fontSize: MIN_FONT_SIZE,
          fontWeight: 600,
          lineHeight: 1.4,
          px: '8px',
          py: '2px',
          borderRadius: `${radius.pill}px`,
          whiteSpace: 'nowrap',
          ...(truncate && { maxWidth: '100%', minWidth: 0 }),
        };
      }}
    >
      {icon}
      {truncate ? (
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
          {label}
        </Box>
      ) : (
        label
      )}
    </Box>
  );
}

export default TonePill;
